import {
  CRITTER_PROJECT_MANIFEST_FILENAME,
  migrateProjectManifest,
  type CritterProjectManifestV1,
} from "../core/migrations/projectManifest";
import { isSafeRelativeArchivePath, normalizeArchivePath } from "./archivePaths";

export type CritterProjectCloudPreflightCode =
  | "manifest-invalid"
  | "source-of-truth-invalid"
  | "file-path-invalid"
  | "file-missing"
  | "asset-path-invalid"
  | "asset-duplicate"
  | "asset-missing"
  | "asset-byte-length-invalid"
  | "asset-sha256-missing"
  | "asset-sha256-invalid"
  | "integrity-policy-not-training-safe"
  | "integrity-incomplete"
  | "integrity-local-only"
  | "server-sha256-verification-required";

export type CritterProjectCloudPreflightIssue = {
  code: CritterProjectCloudPreflightCode;
  message: string;
  path?: string;
};

export type CritterProjectCloudPreflightResult = {
  ok: boolean;
  manifest: CritterProjectManifestV1 | null;
  errors: CritterProjectCloudPreflightIssue[];
  warnings: CritterProjectCloudPreflightIssue[];
  requirements: {
    serverVerifySha256: true;
  };
};

export type CritterProjectArchiveEntries = Record<
  string,
  Uint8Array | ArrayBuffer | string | undefined
>;

const SHA256_HEX_RE = /^[a-f0-9]{64}$/i;

const toArchivePathSet = (paths?: Iterable<string> | null): Set<string> | null => {
  if (!paths) return null;
  const out = new Set<string>();
  for (const path of paths) {
    const normalized = normalizeArchivePath(path);
    if (normalized) out.add(normalized.toLowerCase());
  }
  return out;
};

const pathExists = (archivePaths: Set<string> | null, path: string): boolean =>
  !archivePaths || archivePaths.has(normalizeArchivePath(path).toLowerCase());

const push = (
  target: CritterProjectCloudPreflightIssue[],
  code: CritterProjectCloudPreflightCode,
  message: string,
  path?: string
) => {
  target.push({ code, message, ...(path ? { path } : {}) });
};

const pushLocalIntegrityRepairWarning = (
  warnings: CritterProjectCloudPreflightIssue[],
  manifest: CritterProjectManifestV1
) => {
  if (manifest.integrity.policy === "sha256-required" && manifest.integrity.complete) return;
  push(
    warnings,
    "integrity-local-only",
    "This project was saved with best-effort asset integrity and is local-only. Open it locally and save the project again to rebuild SHA-256 hashes before cloud upload or training."
  );
};

export function validateCritterProjectCloudPreflight(args: {
  manifest: unknown;
  // Production upload callers must pass archivePaths. Omitting it intentionally runs
  // manifest-only validation and cannot catch missing files or missing asset entries.
  archivePaths?: Iterable<string> | null;
}): CritterProjectCloudPreflightResult {
  const manifest = migrateProjectManifest(args.manifest);
  const errors: CritterProjectCloudPreflightIssue[] = [];
  const warnings: CritterProjectCloudPreflightIssue[] = [];
  const archivePaths = toArchivePathSet(args.archivePaths);

  if (!manifest) {
    push(errors, "manifest-invalid", "Project manifest is missing or uses an unsupported schema.");
    return {
      ok: false,
      manifest: null,
      errors,
      warnings,
      requirements: { serverVerifySha256: true },
    };
  }

  if (archivePaths && !pathExists(archivePaths, CRITTER_PROJECT_MANIFEST_FILENAME)) {
    push(
      errors,
      "file-missing",
      "Archive is missing project.json.",
      CRITTER_PROJECT_MANIFEST_FILENAME
    );
  }

  if (manifest.sourceOfTruth !== "critter-project") {
    push(
      errors,
      "source-of-truth-invalid",
      "Cloud training requires critter_project.json to be the native source of truth."
    );
  }

  pushLocalIntegrityRepairWarning(warnings, manifest);

  [
    ["project snapshot", manifest.files.projectSnapshot],
    ["MJCF model", manifest.files.model],
    ["scene config", manifest.files.sceneConfig],
  ].forEach(([label, path]) => {
    if (!isSafeRelativeArchivePath(path)) {
      push(errors, "file-path-invalid", `Project manifest has an unsafe ${label} path.`, path);
      return;
    }
    if (!pathExists(archivePaths, path)) {
      push(errors, "file-missing", `Archive is missing ${label}.`, path);
    }
  });

  if (manifest.integrity.policy !== "sha256-required") {
    push(
      errors,
      "integrity-policy-not-training-safe",
      "Cloud training requires integrity.policy to be sha256-required."
    );
  }
  if (!manifest.integrity.complete) {
    push(
      errors,
      "integrity-incomplete",
      "Cloud training requires integrity.complete to be true before upload."
    );
  }

  const seenAssets = new Set<string>();
  manifest.assets.forEach((asset) => {
    const normalizedPath = normalizeArchivePath(asset.path);
    const normalizedKey = normalizedPath.toLowerCase();
    if (!isSafeRelativeArchivePath(asset.path)) {
      push(
        errors,
        "asset-path-invalid",
        "Asset manifest contains an unsafe asset path.",
        asset.path
      );
      return;
    }
    if (seenAssets.has(normalizedKey)) {
      push(
        errors,
        "asset-duplicate",
        "Asset manifest contains a duplicate asset path.",
        asset.path
      );
    }
    seenAssets.add(normalizedKey);
    if (!pathExists(archivePaths, asset.path)) {
      push(errors, "asset-missing", "Archive is missing a declared asset.", asset.path);
    }
    if (!Number.isInteger(asset.byteLength) || asset.byteLength < 0) {
      push(
        errors,
        "asset-byte-length-invalid",
        "Asset byteLength must be a non-negative integer.",
        asset.path
      );
    }
    if (!asset.sha256) {
      push(
        errors,
        "asset-sha256-missing",
        "Cloud training requires every asset to declare SHA-256.",
        asset.path
      );
    } else if (!SHA256_HEX_RE.test(asset.sha256)) {
      push(
        errors,
        "asset-sha256-invalid",
        "Asset SHA-256 must be a 64-character hex digest.",
        asset.path
      );
    }
  });

  return {
    ok: errors.length === 0,
    manifest,
    errors,
    warnings,
    requirements: { serverVerifySha256: true },
  };
}

const decodeArchiveText = (entry: Uint8Array | ArrayBuffer | string | undefined): string | null => {
  if (typeof entry === "string") return entry;
  if (entry instanceof ArrayBuffer) return new TextDecoder().decode(new Uint8Array(entry));
  if (entry instanceof Uint8Array) return new TextDecoder().decode(entry);
  return null;
};

export function validateCritterProjectArchiveCloudPreflight(
  entries: CritterProjectArchiveEntries
): CritterProjectCloudPreflightResult {
  const archivePaths = Object.entries(entries || {})
    .filter(([path, entry]) => path && entry !== undefined && !String(path).endsWith("/"))
    .map(([path]) => path);
  const manifestEntry = Object.entries(entries || {}).find(
    ([path]) => normalizeArchivePath(path).toLowerCase() === CRITTER_PROJECT_MANIFEST_FILENAME
  );
  const manifestText = decodeArchiveText(manifestEntry?.[1]);
  let manifest: unknown = null;
  if (manifestText) {
    try {
      manifest = JSON.parse(manifestText);
    } catch {
      manifest = null;
    }
  }
  return validateCritterProjectCloudPreflight({ manifest, archivePaths });
}
